import { ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  computed,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { LanguageService } from '../../../core/services/language.service';

@Component({
  selector: 'app-navbar',
  imports: [MatIconModule, TranslateModule],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class Navbar implements AfterViewInit, OnDestroy {
  readonly isHeroVisible = signal(true);
  readonly isScrollingUp = signal(true);
  readonly isNavbarVisible = computed(() => this.isHeroVisible() || this.isScrollingUp());
  readonly isMobileMenuOpen = signal(false);
  readonly isLanguageMenuOpen = signal(false);
  readonly isServicesMenuOpen = signal(false);
  readonly isFreightMenuOpen = signal(false);
  readonly freightForwardingMenu = [
    { key: 'navbar.services_menu.freight_forwarding', icon: 'public' },
    { key: 'navbar.services_menu.air_freight', icon: 'flight_takeoff' },
    { key: 'navbar.services_menu.ocean_freight', icon: 'sailing' },
    { key: 'navbar.services_menu.custom_clearance', icon: 'verified_user' },
  ];
  readonly servicesMenuSecondary = [
    { key: 'navbar.services_menu.heavy_cargo', icon: 'construction' },
    { key: 'navbar.services_menu.duty_exemption', icon: 'percent' },
    { key: 'navbar.services_menu.relocation', icon: 'swap_horiz' },
  ];

  private heroObserver?: IntersectionObserver;
  private lastScrollY = 0;

  constructor(
    private readonly viewportScroller: ViewportScroller,
    private readonly languageService: LanguageService,
    private readonly ngZone: NgZone
  ) { }

  ngAfterViewInit(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.lastScrollY = window.scrollY || 0;
    const heroElement = document.querySelector('app-hero');

    if (!heroElement || typeof IntersectionObserver === 'undefined') {
      this.isHeroVisible.set(false);
      return;
    }

    this.heroObserver = new IntersectionObserver(
      ([entry]) => {
        this.ngZone.run(() => {
          this.isHeroVisible.set(entry?.isIntersecting ?? false);
        });
      },
      { threshold: 0.1 }
    );
    this.heroObserver.observe(heroElement);
  }

  ngOnDestroy(): void {
    this.heroObserver?.disconnect();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const currentY = this.getScrollTop();
    const delta = currentY - this.lastScrollY;

    this.ngZone.run(() => {
      if (currentY <= 0) {
        if (!this.isScrollingUp()) {
          this.isScrollingUp.set(true);
        }
        this.lastScrollY = currentY;
        return;
      }

      if (delta === 0) {
        return;
      }

      const scrollingUp = delta < 0;
      if (scrollingUp !== this.isScrollingUp()) {
        this.isScrollingUp.set(scrollingUp);
      }
      this.lastScrollY = currentY;
    });
  }

  private getScrollTop(): number {
    return (
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0
    );
  }

  setLanguage(lang: 'en' | 'fr' | 'ar'): void {
    this.languageService.setLanguage(lang);
    this.closeAllMenus();
  }

  scrollToQuote(event?: Event): void {
    event?.preventDefault();
    this.viewportScroller.scrollToAnchor('quote');
    this.closeAllMenus();
  }

  toggleMobileMenu(): void {
    const nextState = !this.isMobileMenuOpen();
    this.isMobileMenuOpen.set(nextState);
    if (nextState) {
      this.isLanguageMenuOpen.set(false);
    } else {
      this.isServicesMenuOpen.set(false);
      this.isFreightMenuOpen.set(false);
    }
  }

  toggleLanguageMenu(): void {
    const nextState = !this.isLanguageMenuOpen();
    this.isLanguageMenuOpen.set(nextState);
    if (nextState) {
      this.isServicesMenuOpen.set(false);
    }
  }

  toggleServicesMenu(): void {
    const nextState = !this.isServicesMenuOpen();
    this.isServicesMenuOpen.set(nextState);
    if (!nextState) {
      this.isFreightMenuOpen.set(false);
    }
  }

  toggleFreightMenu(): void {
    this.isFreightMenuOpen.set(!this.isFreightMenuOpen());
  }

  closeAllMenus(): void {
    this.isMobileMenuOpen.set(false);
    this.isLanguageMenuOpen.set(false);
    this.isServicesMenuOpen.set(false);
    this.isFreightMenuOpen.set(false);
  }
}
